import { ReactNode, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { CapsuleButton } from '@ui/components/CapsuleButton';
import { useMediaQuery } from '@providers/MediaQueryProvider';

import { useClubRoles } from '@features/auth/hooks/useClubRoles';
import { useClubCardQuery } from '@features/club/api/hooks';
import { SiteLogoWidget } from '@features/shared/ui/SiteLogo/SiteLogoWidget';
import { useCreateTournamentMutation } from '@features/tournament/api/hooks';
import { UserAuthMenuWidget } from '@features/user/ui/UserAuthMenu/UserAuthMenuWidget';
import { UserSettingsMenuWidget } from '@features/user/ui/UserSettingsMenu/UserSettingsMenuWidget';

import { SiteLayout } from './SiteLayout';

type Props = {
  children: ReactNode;
  clubId: string;
  preset?: 'default' | 'landing' | 'default-full-width';
  backgroundPreset?: 'coloured' | 'plain' | 'coloured-promo-gradient' | 'coloured-red-dawn';
  hideCreateTournament?: boolean;
};

export function SiteLayoutWidget({
  children,
  clubId,
  preset,
  backgroundPreset,
  hideCreateTournament,
}: Props) {
  const isMobile = useMediaQuery() === 'mobile';
  const { onTournamentCreate } = useTournamentCreate(clubId);
  const { isManager } = useClubRoles(clubId);

  const clubQuery = useClubCardQuery(clubId, { staleTime: Infinity });

  return (
    <SiteLayout
      themeColor={clubQuery.data?.themeColor}
      siteLogo={<SiteLogoWidget clubLogoUrl={clubQuery.data?.logoUrl} />}
      preset={preset}
      backgroundPreset={backgroundPreset}
      actions={
        <>
          {isManager && !hideCreateTournament && !isMobile ? (
            <CapsuleButton size="13m-32" onClick={onTournamentCreate}>
              Создать турнир
            </CapsuleButton>
          ) : null}
          <UserSettingsMenuWidget />
          <UserAuthMenuWidget />
        </>
      }
    >
      {children}
    </SiteLayout>
  );
}

function useTournamentCreate(clubId: string) {
  const navigate = useNavigate();
  const { mutateAsync: createTournament } = useCreateTournamentMutation();
  const onTournamentCreate = useCallback(async () => {
    if (!clubId) return;
    const result = await createTournament({ clubId });
    if (result?.tournamentId) {
      navigate({
        to: '/club/$clubId/tournament/$tournamentId/edit',
        params: { clubId, tournamentId: result.tournamentId },
        from: '/',
      });
    }
  }, [navigate, clubId, createTournament]);

  return { onTournamentCreate };
}
